import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Check, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { gstServices } from '../data/services';
import ServiceCard from '../components/common/ServiceCard';

const GST = () => {
  const gstBenefits = [
    'Expert CA team for GST matters',
    'Timely return filing to avoid penalties',
    'Input Tax Credit optimization',
    'E-way bill generation assistance',
    'GST audit support',
    'Monthly compliance reminders',
  ];

  const gstProcess = [
    { step: '01', title: 'Documentation', description: 'Submit required documents online' },
    { step: '02', title: 'Application', description: 'We file your GST application' },
    { step: '03', title: 'Verification', description: 'Verification by tax authorities' },
    { step: '04', title: 'GSTIN', description: 'Receive your GST number' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="gst-page">
      <Helmet>
        <title>GST Registration & Return Filing Services | GK Legal Solutions</title>
        <meta name="description" content="GST Registration, Return Filing, GST Cancellation & Accounting services. Expert CA team for complete GST compliance. Quick processing, avoid penalties." />
        <link rel="canonical" href="https://gklegalsolutions.com/gst" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">GST Services</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mt-3 mb-6">
                Complete GST Solutions for Your Business
              </h1>
              <p className="font-body text-lg text-slate-600 leading-relaxed mb-8">
                From GST registration to return filing, we provide end-to-end GST compliance services to keep your business tax-compliant and penalty-free.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary flex items-center gap-2" data-testid="gst-get-started-btn">
                    Get GST Registration
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+919820669943">
                  <Button variant="outline" className="btn-secondary flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Talk to Expert
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800"
                alt="Tax documents and calculator"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-slate-900">3 Days</p>
                    <p className="font-body text-sm text-slate-600">GST Registration</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our GST Services
            </h2>
            <p className="font-body text-lg text-slate-600">
              Comprehensive GST solutions for businesses of all sizes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {gstServices.map((service) => (
              <ServiceCard key={service.id} service={service} linkTo="/contact" />
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-spacing bg-slate-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              GST Registration Process
            </h2>
            <p className="font-body text-lg text-slate-600">
              Simple 4-step process to get your GST number.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {gstProcess.map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary transition-colors">
                  <span className="font-heading text-2xl font-bold text-primary group-hover:text-white transition-colors">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="font-body text-sm text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Why Choose Us</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-8">
                Expert GST Services You Can Trust
              </h2>
              <div className="space-y-4">
                {gstBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="font-body text-slate-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-primary/5 p-10 rounded-2xl border border-primary/20">
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">GST Registration Documents</h3>
              <ul className="space-y-3 font-body text-slate-700">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  PAN Card of Business/Owner
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Aadhaar Card
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Business Address Proof
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Bank Account Details
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Photograph
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Business Registration Proof
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Get Your GST Registration Today
          </h2>
          <p className="font-body text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Stay compliant with GST laws and avoid penalties. Get expert assistance now!
          </p>
          <Link to="/contact">
            <Button className="bg-white text-primary hover:bg-slate-100 px-8 py-3 font-semibold">
              Apply for GST Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GST;
